import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * Correct / most balanced answer = 1 point
 * Other options = 0 points
 * Max Score = 15
 */

// Index of correct option for each question
const correctAnswers = [
  1, // Q1
  0, // Q2
  0, // Q3
  0, // Q4
  0, // Q5
  0, // Q6
  0, // Q7
  0, // Q8
  0, // Q9
  0, // Q10
  0, // Q11
  0, // Q12
  0, // Q13
  0, // Q14
  0, // Q15
];

const quizData = [
  {
    question: "Which meal is generally recommended to be the heaviest of the day?",
    options: ["Breakfast", "Lunch", "Dinner"],
  },
  {
    question: "Regularly eating very heavy dinners is most likely to affect…",
    options: [
      "Digestion and sleep quality",
      "Only body weight",
      "Morning energy levels only",
    ],
  },
  {
    question: "Skipping meals frequently can eventually disturb…",
    options: [
      "Hunger and digestion signals",
      "Metabolism positively",
      "Fat loss efficiency",
    ],
  },
  {
    question: "Eating meals at very irregular times usually leads to…",
    options: [
      "Confused digestive rhythm",
      "Better flexibility in digestion",
      "No noticeable impact",
    ],
  },
  {
    question: "Which description best matches true physical hunger?",
    options: [
      "Gradual and steady",
      "Sudden and urgent",
      "Emotion-driven",
    ],
  },
  {
    question: "Cravings are most commonly influenced by…",
    options: [
      "Stress, sleep, and food quality",
      "Lack of discipline",
      "The body needing sugar",
    ],
  },
  {
    question: "Warm, freshly cooked food is often considered easier to digest because…",
    options: [
      "It supports digestive activity",
      "It has fewer calories",
      "It automatically reduces portions",
    ],
  },
  {
    question: "Regular consumption of very cold food or drinks may…",
    options: [
      "Slow digestion for some people",
      "Improve digestion",
      "Have no effect at all",
    ],
  },
  {
    question: "Leftover food that is reheated multiple times is generally thought to…",
    options: [
      "Lose freshness and digestibility",
      "Become more nutritious",
      "Be equal to freshly cooked food",
    ],
  },
  {
    question: "Fruits are generally easier to digest when eaten…",
    options: [
      "Alone or with light meals",
      "Right after heavy meals",
      "Late at night",
    ],
  },
  {
    question: "Eating very quickly mainly affects…",
    options: [
      "Digestion and fullness cues",
      "Only calorie intake",
      "Nutrient absorption positively",
    ],
  },
  {
    question: "Drinking small amounts of water during meals is…",
    options: [
      "Usually fine for most people",
      "Always harmful",
      "Necessary in large quantities",
    ],
  },
  {
    question: "Which statement about fats and oils is most accurate?",
    options: [
      "Both quality and quantity matter",
      "All fats are unhealthy",
      "Healthy fats can be eaten freely",
    ],
  },
  {
    question: "The idea that “oil-free food is always healthier” is…",
    options: [
      "An oversimplification",
      "Completely true",
      "Proven by Ayurveda",
    ],
  },
  {
    question: "A balanced understanding of healthy eating includes…",
    options: [
      "What, when, and how we eat",
      "Calories alone",
      "Following strict food rules",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */
const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
    <div
      className="
        relative
        overflow-hidden
        rounded-[34px]
        max-w-5xl
        w-full
        h-[620px]
        shadow-[0_30px_80px_rgba(20,10,0,.12)]
      "
    >
      <img
        src="/assets/knowyourself/quiz/nutrition.jpg"
        alt="Nutrition Assessment"
        className="
          w-full
          h-full
          object-cover
          brightness-[.84]
          saturate-[.9]
        "
      />

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/90
          via-black/35
          to-transparent
        "
      />

      <div className="absolute inset-x-0 bottom-0 p-6">
        <div
          className="
            rounded-[28px]
            bg-white/10
            backdrop-blur-2xl
            border
            border-white/10
            p-8
          "
        >
          <div
            className="
              inline-flex
              px-4
              py-2
              rounded-full
              bg-white/10
              backdrop-blur-xl
              border
              border-white/10
              text-white
              text-xs
              tracking-[.08em]
              mb-5
            "
          >
            NUTRITION ASSESSMENT
          </div>

          <h1
            className="
              text-[38px]
              md:text-[56px]
              leading-[1.05]
              text-white
              mb-5
            "
          >
            Nutrition & Healthy Eating
          </h1>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-8
            "
          >
            Discover how well you understand
            healthy eating, digestion, meal timing,
            food quality, and common nutrition myths.
          </p>

          <Button
            variant="primary"
            text="Start Assessment"
            onClick={onStart}
          />
        </div>
      </div>
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */
const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans, i) => sum + (ans === correctAnswers[i] ? 1 : 0),
    0
  );

  let title = "";
  let message = "";

  if (totalScore >= 12) {
    title = "🥗 Strong Nutrition Awareness";

    message =
      "You understand healthy eating as a balance of digestion, meal timing, food quality, and daily habits. You can identify common myths and make informed nutrition choices. Focus on consistency over perfection.";
  } else if (totalScore >= 7) {
    title = "🌱 Partial Awareness";

    message =
      "You understand several healthy eating principles but may still carry some mixed or outdated beliefs. This is where many people are. Focus on digestion-first thinking, meal regularity, and simplicity.";
  } else {
    title = "🍃 Knowledge Needs Strengthening";

    message =
      "You may be influenced by nutrition trends, extremes, or conflicting advice. This isn't a problem—it's simply an opportunity to build stronger foundations before exploring advanced strategies.";
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
      <div
        className="
          relative
          overflow-hidden
          rounded-[34px]
          max-w-5xl
          w-full
          h-[620px]
          shadow-[0_30px_80px_rgba(20,10,0,.12)]
        "
      >
        <img
          src="/assets/knowyourself/quiz/result.jpg"
          alt="Nutrition Result"
          className="
            w-full
            h-full
            object-cover
            brightness-[.84]
            saturate-[.9]
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/90
            via-black/35
            to-transparent
          "
        />

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div
            className="
              rounded-[28px]
              bg-white/10
              backdrop-blur-2xl
              border
              border-white/10
              p-8
            "
          >
            <div
              className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-white/10
                backdrop-blur-xl
                border
                border-white/10
                text-white
                text-xs
                tracking-[.08em]
                mb-5
              "
            >
              YOUR RESULT
            </div>

            <h2
              className="
                text-[34px]
                md:text-[52px]
                leading-[1.05]
                text-white
                mb-4
              "
            >
              {title}
            </h2>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-4
              "
            >
              Nutrition Score: {totalScore} / 15
            </p>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-8
                max-w-3xl
              "
            >
              {message}
            </p>

            <Button
              variant="primary"
              text="Retake Assessment"
              onClick={onRetake}
            />
          </div>
        </div>
      </div>
    </div>
  );
};


/* -------------------- MAIN COMPONENT -------------------- */

const NutritionKnowledgeQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default NutritionKnowledgeQuiz;
